import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Search, Download, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROCEDURE_OPTIONS = [
  { value: 'civil', label: 'Πολιτική' },
  { value: 'administrative', label: 'Διοικητική' },
  { value: 'criminal', label: 'Ποινική' },
];

const DOC_OPTIONS = [
  { value: 'lawsuit', label: 'Αγωγή' },
  { value: 'special_lawsuit', label: 'Αγωγή (Ειδική Διαδικασία)' },
  { value: 'voluntary_app', label: 'Αίτηση Εκουσίας' },
  { value: 'interim_app', label: 'Αίτηση Ασφαλιστικών' },
  { value: 'appeal', label: 'Έφεση' },
  { value: 'appeal_peace', label: 'Έφεση Ειρηνοδικείου' },
  { value: 'appeal_single_first', label: 'Έφεση Μονομελούς' },
  { value: 'cassation', label: 'Αίτηση αναίρεσης' },
  { value: 'cassation_admin', label: 'Αίτηση αναίρεσης (ΣτΕ)' },
  { value: 'injunction', label: 'Διαταγή Πληρωμής' },
  { value: 'objection', label: 'Ανακοπή' },
  { value: 'objection_dp', label: 'Ανακοπή κατά ΔΠ' },
  { value: 'objection_933', label: 'Ανακοπή 933' },
  { value: 'objection_default', label: 'Ανακοπή ερημοδικίας' },
  { value: 'appeal_lawsuit', label: 'Αγωγή (Διοικητικό)' },
  { value: 'admin_prosfygi', label: 'Προσφυγή' },
  { value: 'cancellation_request', label: 'Αίτηση ακύρωσης' },
  { value: 'criminal_appeal', label: 'Έφεση Ποινική' },
  { value: 'criminal_cassation', label: 'Αναίρεση Ποινική' },
];

const DESC_OPTIONS = [
  { value: 'debt', label: 'Αγωγή χρέους' },
  { value: 'property', label: 'Εμπράγματη αγωγή' },
  { value: 'fraud', label: 'Καταδολίευση' },
  { value: 'inheritance', label: 'Κληρονομική' },
  { value: 'divorce', label: 'Διαζύγιο' },
  { value: 'alimony', label: 'Διατροφή' },
  { value: 'custody', label: 'Επιμέλεια' },
  { value: 'lease', label: 'Μισθωτική' },
  { value: 'labor', label: 'Εργατική' },
  { value: 'tort', label: 'Αδικοπραξία' },
  { value: 'contract', label: 'Συμβατική' },
  { value: 'unjust_enrichment', label: 'Αδικαιολόγητος πλουτισμός' },
  { value: 'compensation', label: 'Αποζημίωση' },
  { value: 'ownership', label: 'Αναγνώριση κυριότητας' },
  { value: 'insurance', label: 'Ασφαλιστική' },
  { value: 'medical', label: 'Ιατρική αμέλεια' },
  { value: 'traffic', label: 'Τροχαίο' },
  { value: 'other', label: 'Άλλο' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(iso);
  }
};

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const removeAccents = (s) => (s || '').replace(/Ά/g, 'Α').replace(/Έ/g, 'Ε').replace(/Ή/g, 'Η').replace(/Ί/g, 'Ι').replace(/Ό/g, 'Ο').replace(/Ύ/g, 'Υ').replace(/Ώ/g, 'Ω').replace(/Ϊ/g, 'Ι').replace(/Ϋ/g, 'Υ');

export default function AdminTemplates() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    client_id: 'all',
    procedure_type: 'all',
    case_description_type: 'all',
    q: '',
  });

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => {
        const an = a.client_type === 'legal' ? (a.company_name || '') : `${a.surname || ''} ${a.name || ''}`.trim();
        const bn = b.client_type === 'legal' ? (b.company_name || '') : `${b.surname || ''} ${b.name || ''}`.trim();
        return an.localeCompare(bn, 'el');
      });
      setClients(list);
    } catch {}
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.client_id && filters.client_id !== 'all') params.client_id = filters.client_id;
      if (filters.procedure_type && filters.procedure_type !== 'all') params.procedure_type = filters.procedure_type;
      if (filters.case_description_type && filters.case_description_type !== 'all') params.case_description_type = filters.case_description_type;
      if (filters.q.trim()) params.q = filters.q.trim();
      const res = await axios.get(`${API}/templates`, { params, withCredentials: true });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchItems();
  }, []);

  const handleDownload = async (item) => {
    try {
      const res = await axios.get(`${API}/templates/${item.document_id}/download`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: item.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error('Σφάλμα κατά τη λήψη');
    }
  };

  const labelOf = (list, value) => {
    const found = list.find((o) => o.value === value);
    return found ? found.label : value || '—';
  };

  const resetFilters = () =>
    setFilters({ client_id: 'all', procedure_type: 'all', case_description_type: 'all', q: '' });

  const clientLabel = (c) => {
    if (!c) return '';
    const nm = c.client_type === 'legal' ? (c.company_name || '') : `${c.surname || ''} ${c.name || ''}`.trim();
    return nm ? nm.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '—';
  };

  return (
    <div className="min-h-screen bg-[#f7f2e7]">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} title="Επιστροφή"
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-stone-300 bg-white text-slate-600 hover:bg-stone-50 hover:text-slate-900 transition-colors" data-testid="templates-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-700" />
              Έγγραφα
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3" data-testid="templates-filters">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Φίλτρα αναζήτησης
            </h2>
            <button onClick={resetFilters} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <X className="w-3 h-3" /> Καθαρισμός
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">{removeAccents('Πελάτης')}</Label>
              <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v })}>
                <SelectTrigger className="h-9 bg-white border-stone-300 text-sm" data-testid="filter-client">
                  <SelectValue placeholder="Όλοι" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλοι</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{clientLabel(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{removeAccents('Διαδικασία')}</Label>
              <Select value={filters.procedure_type} onValueChange={(v) => setFilters({ ...filters, procedure_type: v })}>
                <SelectTrigger className="h-9 bg-white border-stone-300 text-sm" data-testid="filter-procedure">
                  <SelectValue placeholder="Όλες" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες</SelectItem>
                  {PROCEDURE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{removeAccents('Περιγραφή υπόθεσης')}</Label>
              <Select value={filters.case_description_type} onValueChange={(v) => setFilters({ ...filters, case_description_type: v })}>
                <SelectTrigger className="h-9 bg-white border-stone-300 text-sm" data-testid="filter-description">
                  <SelectValue placeholder="Όλες" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες</SelectItem>
                  {DESC_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{removeAccents('Αναζήτηση με λέξεις')}</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="filename / περιγραφή..."
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                  className="h-9 pl-8 bg-white border-stone-300 text-sm"
                  data-testid="filter-q"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={fetchItems} disabled={loading} className="bg-red-700 hover:bg-red-800 text-white" data-testid="filter-apply-btn">
              {loading ? 'Αναζήτηση...' : 'Αναζήτηση'}
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Αποτελέσματα ({items.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="templates-table">
              <thead className="bg-stone-50 text-slate-600 text-xs tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left w-1/3">{removeAccents('Filename')}</th>
                  <th className="px-3 py-2 text-left">{removeAccents('Πελάτης')}</th>
                  <th className="px-3 py-2 text-left">{removeAccents('Διαδικασία')}</th>
                  <th className="px-3 py-2 text-left">{removeAccents('Περιγραφή Υπόθεσης')}</th>
                  <th className="px-3 py-2 text-center">{removeAccents('Ημερομηνία')}</th>
                  <th className="px-3 py-2 text-center">{removeAccents('Λήψη')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Φόρτωση...</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Δεν βρέθηκαν δικόγραφα</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} data-testid={`template-row-${it.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900 truncate" title={it.filename}>{it.filename}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{it.client?.name || '—'}</td>
                    <td className="px-3 py-2 text-xs">{labelOf(PROCEDURE_OPTIONS, it.procedure_type)}</td>
                    <td className="px-3 py-2 text-xs">
                      {it.case_description_type === 'other' && it.case_description_custom
                        ? it.case_description_custom
                        : labelOf(DESC_OPTIONS, it.case_description_type)}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-600">{formatDate(it.created_at)}<br /><span className="text-[10px] text-slate-400">{formatBytes(it.size)}</span></td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleDownload(it)}
                        className="p-1.5 hover:bg-red-50 rounded text-red-700"
                        title="Λήψη"
                        data-testid={`template-download-${it.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
